import React from 'react';
import { clsx } from 'clsx';
import { IconType } from 'react-icons';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  icon?: IconType;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, icon: Icon, iconPosition = 'left', fullWidth = true, style, ...props }, ref) => {
    const hasIcon = !!Icon;
    const hasError = !!error;

    return (
      <div className={clsx('relative', { 'w-full': fullWidth })}>
        {hasIcon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Icon className="h-4 w-4 transition-colors duration-150" style={{ color: hasError ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }} />
          </div>
        )}

        <input
          className={clsx(
            'flex h-9 w-full rounded-lg border px-4 py-2 text-sm',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'placeholder:opacity-60',
            'focus-visible:outline-none',
            { 'pl-9': hasIcon && iconPosition === 'left', 'pr-9': hasIcon && iconPosition === 'right' },
            className
          )}
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)',
            color: 'var(--theme-input-text)',
            transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
            ...style
          }}
          onFocus={(e) => {
            if (!hasError && !props.disabled) {
              e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              e.currentTarget.style.boxShadow = '0 0 0 3px var(--ring-color)';
            }
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)';
            e.currentTarget.style.boxShadow = 'none';
            props.onBlur?.(e);
          }}
          ref={ref}
          {...props}
        />

        {hasIcon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icon className="h-4 w-4 transition-colors duration-150" style={{ color: hasError ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }} />
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
